import React, { Component, Fragment } from 'react';
import { connect } from "react-redux";
import { GroundFloorDurableFunctionInstance } from "../../../models/DurableFunctionInstance";
import Actions from "../../../redux/Actions";
import DispatchProps from "../../../utils/DispatchProps";
import { IRootState } from "../../../store";
import { WorkloadInstanceQuery } from "../../../models/WorkloadInstanceQuery";
import GroundFloorDurableFunctionInstanceList from "../../GroundFloorDurableFunctionInstanceList";
import * as H from "history";
import WorkloadQueryControls from "./workloads/WorkloadQueryControls";
import ApiService from "../../ApiService";
import School from "../../../models/School";
import { friendlySchoolName, openFileDownload } from "../../../utils/Utils";
import SchoolList from "../../SchoolList";
import { Link } from "react-router-dom";
import {CommandBarButton, IDropdownOption, MessageBarType} from "office-ui-fabric-react";
import WorkloadTriggerControls from "../../TriggerWorkloadControls";
import SchoolPageTitle from "../../SchoolPageTitle";
import {buildIndexedSchools} from "../../../utils/IndexedSchools";
import AdminSchoolMenu from "../../AdminSchoolMenu";
import {History} from "history";
import LinkCard from "../../LinkCard";

interface SchoolDashboardPageProps {
    tenantId: string;
    schoolCode: string;
    queryString?: string;

    history: H.History;
    apiService: ApiService;
}

interface SchoolDashboardPageState {
    school?: School;
    workloadInstanceQuery: WorkloadInstanceQuery;
    workloadInstances?: GroundFloorDurableFunctionInstance[],
    workloadAndPlaylistOptions?: IDropdownOption[];
    showDeleteDialog: boolean;
    isDeleting: boolean;
}

class SchoolDashboardPage extends Component<SchoolDashboardPageProps & DispatchProps, SchoolDashboardPageState> {
    static displayName = SchoolDashboardPage.name;

    constructor(props: SchoolDashboardPageProps & DispatchProps) {
        super(props);

        this.state = {
            workloadInstanceQuery: WorkloadInstanceQuery.withDefaults(),
            workloadInstances: undefined,
            workloadAndPlaylistOptions: undefined,
            showDeleteDialog: false,
            isDeleting: false
        }
    }

    async componentDidUpdate(prevProps: Readonly<SchoolDashboardPageProps & DispatchProps>, prevState: Readonly<SchoolDashboardPageState>, snapshot?: any): Promise<void> {
        
        if (this.props.tenantId !== prevProps.tenantId || this.props.schoolCode !== prevProps.schoolCode) {
            await this.getSchool();
        }
        
        if(this.props.queryString !== prevProps.queryString)
        {
            this.setQueryFromUrl();
        }
        
        if (this.state.workloadInstanceQuery !== prevState.workloadInstanceQuery) {
            this.setState({
                workloadInstances: undefined
            });
            const instances = await this.props.apiService.getGroundFloorDurableFunctionInstances(this.state.workloadInstanceQuery);
            this.setState({
                workloadInstances: instances
            });
        }

    }

    async componentDidMount() {

        this.setQueryFromUrl();

        if (this.props.tenantId && this.props.schoolCode)
        {
            await this.getSchool();
        }
        
        const workloadAndPlaylistOptions = await WorkloadQueryControls.fetchWorkloadAndPlaylistOptions(this.props.apiService);
        this.setState({
            workloadAndPlaylistOptions
        });
    }
    
    private async getSchool() {
        this.setState({
            school: undefined
        });
        const school = await this.props.apiService.getSchoolAdmin(this.props.tenantId,this.props.schoolCode);
        this.setState({
            school
        });
    }

    private setQueryFromUrl(): void {
        
        if(this.props.tenantId && this.props.schoolCode)
        {
            let queryForSchool;
            
            if (this.props.queryString)
            {
                queryForSchool = WorkloadInstanceQuery.fromQueryString(this.props.queryString);
                queryForSchool.tenantId = this.props.tenantId;
                queryForSchool.schoolCode = this.props.schoolCode;
            }
            else
            {
                queryForSchool = WorkloadInstanceQuery.withDefaultsForSchool(this.props.tenantId,this.props.schoolCode);
                queryForSchool.tenantId = this.props.tenantId;
                queryForSchool.schoolCode = this.props.schoolCode;
            }
            
            
            this.setState({
                workloadInstanceQuery: queryForSchool
            });
        }
    }

    private onQueryChanged = (query: WorkloadInstanceQuery): void => {

        const newQueryString = ApiService.buildQueryString(this.state.workloadInstanceQuery);
        this.props.history.push({ search: newQueryString });

        this.setState({
            workloadInstanceQuery: query
        });
    };

    private resetQuery = (): void => {

        if (this.props.tenantId && this.props.schoolCode)
        {
            this.props.history.push({ search: "" });
            this.setState({
                workloadInstanceQuery: WorkloadInstanceQuery.withDefaultsForSchool(this.props.tenantId,this.props.schoolCode)
            });
        }
    };

    private buildConsentPrompt(): JSX.Element {
        const linkUrl = `https://login.microsoftonline.com/${this.state.school!.tenantName}.onmicrosoft.com/adminconsent?client_id=${this.state.school!.clientId}&state=empty`;
        return (
            <a href={linkUrl} className="p-right-1rem p-top-05rem m-left-auto text-tiny text-align-right " target={
                "_blank"} rel="noopener noreferrer">
                Don't forget to authorize workloads for this tenant
            </a>
        );
    }
    
    render() {

        const triggerWorkloadControls = this.state.school && this.state.workloadAndPlaylistOptions ? <WorkloadTriggerControls school={this.state.school} workloadAndPlaylistOptions={this.state.workloadAndPlaylistOptions} onWorkloadTriggered={this.resetQuery} /> : undefined;
        const title = this.state.school ? friendlySchoolName(this.state.school) : "";
        
        const schools = this.state.school ? [this.state.school] : [];
        const indexedSchools = buildIndexedSchools(schools);

        const workloadTableHeaderExtra = (
            <Fragment>
                <Link to={`/cdb-admin/schools/${this.props.tenantId}/${this.props.schoolCode}/workload-schedules`}>
                    <CommandBarButton
                        iconProps={{iconName: "Calendar"}}
                        text="Manage Schedules"
                        onClick={() => {}}
                    />
                </Link>
                               
                <CommandBarButton
                    text={"Download Summary Data"}
                    iconProps={{ iconName: "DownloadDocument" }}
                    onClick={async () => {
                        try {
                            if (this.state.workloadInstanceQuery) {
                                let result;

                                result = await this.props.apiService.getGroundFloorDurableFunctionInstancesDownload(this.state.workloadInstanceQuery);

                                await openFileDownload(result)

                                this.props.dispatch(Actions.showMessageBar(`Downloading summary data`, MessageBarType.success));
                            }
                        }
                        catch (e) {
                            console.log(e);
                            this.props.dispatch(Actions.showMessageBar(`Error downloading summary data. If this problem persists please contact support. ${e.message}`, MessageBarType.error));
                        }
                    }} />
                {triggerWorkloadControls}
            </Fragment>
        );
        
        return (
            <Fragment>
                <SchoolPageTitle tenantId={this.props.tenantId} schoolCode={this.props.schoolCode} title="Workloads"/>
                <AdminSchoolMenu tenantId={this.props.tenantId} schoolCode={this.props.schoolCode}/>


                <SchoolList title={title} enableRowLinks={false} schools={schools} loading={!this.state.school}>
                    
                </SchoolList>

                <div className={"flex-row"}>
                    
                    <LinkCard title={"Configure"}
                              disabled={false}
                              to={`/cdb-admin/schools/${this.props.tenantId}/${this.props.schoolCode}/configure`}
                              iconName={"ConfigurationSolid"}
                              description={"Configure workload settings"}/>


                    <LinkCard title={"Tools"}
                              disabled={false}
                              to={`/cdb-admin/schools/${this.props.tenantId}/${this.props.schoolCode}/tools-menu`}
                              iconName={"DeveloperTools"}
                              description={"Tools"}/>
                </div>

                <div className={"flex-row flex--flow-wrap flex--align-items-flex-end"}>
                    {this.state.workloadAndPlaylistOptions ?
                        <WorkloadQueryControls workloadInstanceQuery={this.state.workloadInstanceQuery}
                                               workloadAndPlaylistOptions={this.state.workloadAndPlaylistOptions}
                                               indexedSchools={indexedSchools} onQueryChanged={this.onQueryChanged}
                                               onResetQueryClicked={this.resetQuery}
                                               schoolOptionDisabled={true}/> : null}
                    {this.state.school ? this.buildConsentPrompt() : null}
                </div>

                <GroundFloorDurableFunctionInstanceList instances={this.state.workloadInstances || []}
                                                        isLoading={this.state.workloadInstances === undefined}
                                                        indexedSchools={indexedSchools}
                                                        title={`Workloads For ${this.state.school ? friendlySchoolName(this.state.school) : ""}`}
                                                        enableRowLinks={true} headerExtra={workloadTableHeaderExtra}/>
            </Fragment>
        );
    }
}

export default connect(
    (state: IRootState, ownProps: { history: History, match: any }): SchoolDashboardPageProps => {

        const tenantId = ownProps.match.params.tenantId;
        const schoolCode = ownProps.match.params.schoolCode;

        return {
            tenantId: tenantId,
            schoolCode: schoolCode,
            history: ownProps.history,
            queryString: ownProps.history.location.search,
            apiService: state.auth.apiService
        };
    }
)(SchoolDashboardPage);